import {Banner, Button, Form, TextField, addToast} from '@albert-io/atomic';
import React, {useCallback, useState} from 'react';
import {Helmet} from 'react-helmet';
import validator from 'validator';

import {title} from 'lib/head';

import {useForgotPasswordMutation} from './ForgotPassword.queries.generated';

import './assets/forgot-password.scss';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [hasError, setError] = useState(false);
  const [forgotPasswordMutation] = useForgotPasswordMutation();

  const validate = useCallback(() => {
    setError(!validator.isEmail(email));
  }, [email]);

  const sendResetLink = useCallback(
    async (e) => {
      e.preventDefault();
      if (!email || hasError) {
        return;
      }
      try {
        await forgotPasswordMutation({
          variables: {
            email
          }
        });

        addToast({
          color: 'positive',
          title: 'Reset link is sent successfully!',
          message: `We've sent you a reset link to the email you entered.`
        });
      } catch (_err) {
        addToast({
          color: 'negative',
          title: 'Unable to reset password.',
          message: 'We were unable to send a reset password email to your provided address.'
        });
      }
    },
    [email, hasError, forgotPasswordMutation]
  );

  const metaDescription = `Enter your email address and we'll send you a reset link.`;

  return (
    <div className='forgot-password-wrapper'>
      <Helmet>{title('Albert | Reset Password')}</Helmet>
      <div className='forgot-password__content mt-x5 rounded-x1 border border-solid border-border-primary background-primary p-x4 shadow-l3'>
        <Banner className='forgot-password__banner mb-x2'>
          <Banner.Icon icon='info-circle' iconStyle='regular' />
          <p>
            If you signed up with a password provided by your teacher and can&apos;t access your
            email address, please ask your teacher to reset your password for you. Once they&apos;ve
            given you the new password, please log in.
          </p>
        </Banner>
        <Form onSubmit={sendResetLink}>
          <Form.Header subtitle={metaDescription}>Reset password</Form.Header>
          <TextField
            border
            name='email'
            label='Email'
            type='email'
            autoComplete='email'
            required
            maxLength={128}
            defaultValue={email}
            error={hasError}
            message={hasError ? 'Please enter a valid email address.' : null}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={validate}
          />
          <Button type='submit' className='forgot-password__submit mt-x2'>
            Get reset link
          </Button>
        </Form>
        <div className='forgot-password__instructions mt-x2 rounded-x1 border border-solid border-border-primary background-subtle p-x3'>
          <b>
            If you can&apos;t find the reset password email in your inbox, please try one of the
            following:
          </b>
          <ol className='pl-x2'>
            <li className='mb-x1'>
              Check your spam folder and double-check your inbox for a message titled &quot;Reset
              your Albert.io password&quot;
            </li>
            <li className='mb-x1'>
              Contact your school administrator or IT administrator and ensure they are not blocking
              emails coming from the albert.io domain.
            </li>
            <li className='mb-x1'>Try resubmitting the form above.</li>
          </ol>
        </div>
      </div>
    </div>
  );
}
